import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { importPortalLead, parsePortalText } from '../lib/api';
import { friendlyError } from '../lib/format';
import type { PortalParse } from '../lib/types';
import { Button, ErrorText, Modal, SelectField, TextArea, TextField } from '../components/ui';

const PORTALS = ['99acres', 'MagicBricks', 'Housing.com', 'Square Yards', 'NoBroker', 'CommonFloor', 'Makaan', 'Other portal'];

// Paste a portal enquiry email; ReAstra reads the details by fixed rules, you check them, then the lead is made.
export function PortalImportModal({ onClose, onImported }: { onClose: () => void; onImported: (leadId: string, message: string) => void }) {
  const { projects } = useAuth();
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState<PortalParse | null>(null);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [projectId, setProjectId] = useState('');
  const [portal, setPortal] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function read(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const p = await parsePortalText(text);
      setParsed(p);
      setPhone(p.phone ?? ''); setName(p.name ?? ''); setEmail(p.email ?? ''); setProjectId(p.project_id ?? '');
      setPortal(p.portal ?? 'Other portal'); setMessage(p.message ?? '');
    } catch (err) { setError(friendlyError(err)); } finally { setBusy(false); }
  }

  async function create(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await importPortalLead({ phone, name, projectId: projectId || null, portal, message, email });
      onImported(r.lead_id, r.is_new
        ? `Lead created from ${portal}${r.owner_name ? ` and assigned to ${r.owner_name}` : ', waiting for an owner'}.`
        : `This buyer already had an open lead for that project, so the enquiry was added to it.`);
    } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title="Import a portal enquiry" wide>
      {!parsed ? (
        <form onSubmit={read} className="flex flex-col gap-4">
          <TextArea label="Paste the enquiry email" required rows={10} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={'From: MagicBricks\nName: Anil Verma\nMobile: 98110 22334\nProject: Tara Greens\nMessage: Looking for 3 BHK'}
            hint="From 99acres, MagicBricks, Housing.com and others. Paste one enquiry at a time; you check what was read before anything is saved." />
          <ErrorText>{error}</ErrorText>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Reading…' : 'Read it'}</Button>
          </div>
        </form>
      ) : (
        <form onSubmit={create} className="flex flex-col gap-4">
          {parsed.problems.length > 0 && <p className="rounded-lg border border-warn bg-warnSoft px-3 py-2 text-sm text-warn">{parsed.problems.join(' · ')}. Fill it in below.</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Phone" required value={phone} onChange={(e) => setPhone(e.target.value)} />
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <SelectField label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">Not decided</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </SelectField>
            <SelectField label="Portal" value={portal} onChange={(e) => setPortal(e.target.value)}>
              {PORTALS.map((p) => <option key={p}>{p}</option>)}
            </SelectField>
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <TextArea label="What they asked" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
          <p className="text-xs text-muted">The lead is assigned by your rules, like any other enquiry. The buyer is not messaged.</p>
          <ErrorText>{error}</ErrorText>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setParsed(null)}>Back</Button>
            <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Create lead'}</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
